<script setup>
import { onBeforeMount, ref, watchEffect } from 'vue';

import { Amplify } from 'aws-amplify';
import { events } from 'aws-amplify/data';
import QRCode from 'qrcode';
import { useBreakStore } from '@/stores/break';

// // Utils imports
import identifiers from '@/utils/identifiers';

// // Store management imports
import { useTrackerStore } from '@/stores/tracker';

const props = defineProps({
  // // @Params
  // // qrSuccess: 'sp' = Spin (default), 'em' = Emit
  targetUrl: {
    type: String,
    required: true,
  },
  interactiveBreakUuid: {
    type: String,
    required: true,
  },
  qrSuccess: {
    type: String,
    default: 'sp',
  },
  productUuid: {
    type: String,
    required: true,
  },
  adId: {
    type: String,
    required: true,
  },
  barcodeForegroundColor: {
    type: String,
    // required: true,
    default: '#041F6A',
    // default: 'rgba(4, 31, 106, 1)',
  },
  barcodeBackgroundColor: {
    type: String,
    // required: true,
    default: '#FFFFFF',
    // default: 'rgba(255, 255, 255, 1)',
  },
});

const emit = defineEmits(['scanSuccess']);

const storeBreak = useBreakStore();
const storeTracker = useTrackerStore();

const qrImageDataUrl = ref();
const hasBeenScannedRecently = ref(false);

const appInstance = import.meta.env.VITE_APP_ENV;
const shopUrlPrefix = import.meta.env.VITE_APP_SHOP_PREFIX;
const ampUrl = import.meta.env.VITE_APP_AMP_URL;
const ampKey = import.meta.env.VITE_APP_AMP_KEY;
const catchErrorPrefix = import.meta.env.VITE_APP_CATCH_ERROR_PREFIX;
const qrIdent = identifiers.generateV4Uuid();
storeBreak.qrIdent = qrIdent;
const channelName = `/default/${qrIdent}`;

// console.log(`Project mode: ${appMode}`);
// console.log(`Project instance: ${appInstance}`);

// // BarcodeQr setup functions

Amplify.configure({
  API: {
    Events: {
      endpoint: ampUrl,
      region: 'us-east-1',
      defaultAuthMode: 'apiKey',
      apiKey: ampKey,
    },
  },
});

const setScanSuccessEffect = () => {
  let qrBarcodeSuccessResults = 'spinEvenlyEffect';

  if (props.qrSuccess === 'em') {
    qrBarcodeSuccessResults = 'emitSuccess';
  }

  // console.log(`setScanSuccessEffect() qrBarcodeSuccessResults: ${qrBarcodeSuccessResults}`);

  return qrBarcodeSuccessResults;
};

// // BarcodeQr functions

const createRedirectUrl = (
  urlParam,
  barcodeIdentifierParam = qrIdent,
  breakIdentifierParam = props.interactiveBreakUuid,
  productUuid = props.productUuid,
  adId = props.adId,
) => {
  const prodUrl = new URL(urlParam);
  if (prodUrl.hostname.includes('fourthwall.com')) {
    prodUrl.searchParams.append('utm_content', barcodeIdentifierParam);
  }
  return `${shopUrlPrefix}/?embedded_url=${encodeURIComponent(prodUrl.href)}&qr_id=${barcodeIdentifierParam}&ib_uuid=${breakIdentifierParam}&product_uuid=${productUuid}&ad_id=${adId}`;
};

const qrUrl = ref(
  createRedirectUrl(
    props.targetUrl,
    qrIdent,
    props.interactiveBreakUuid,
    props.productUuid,
    props.adId,
  ),
);

events.connect(channelName).then((channel) => {
  channel.subscribe({
    next: (receivedData) => {
      hasBeenScannedRecently.value = true;

      // // Update beacon with accept invitation event
      if (storeTracker.isIframe && ['dev', 'stage', 'prod'].includes(appInstance)) {
        if (storeTracker.isBreakInteracted === false) {
          storeTracker.isBreakInteracted = true;
          if (
            storeTracker.trackingInfo &&
            storeTracker.trackingInfo.trackingEvents &&
            storeTracker.trackingInfo.trackingEvents.length > 0
          ) {
            storeTracker.trackerBeacon.beaconManager.sendTrackingEvent(
              storeTracker.trackingInfo,
              'acceptInvitation',
              props.interactiveBreakUuid,
            );
          }
        }
      }

      console.log(`Received data: ${receivedData}`);
    },
    error: (errorResponse) => {
      fetch(
        `${catchErrorPrefix}${appInstance}/appSyncError?ib_uuid=${props.interactiveBreakUuid}&error=${errorResponse}`,
      );
    },
  });

  // console.log(`Subscribed to ${channelName}`);
});

// // BarcodeQr lifecycle hooks

onBeforeMount(() => {
  setScanSuccessEffect();
});

// // BarcodeQr watchers

watchEffect(() => {
  if (hasBeenScannedRecently.value) {
    emit('scanSuccess', true);

    setTimeout(() => {
      // console.log(`Stopping animation`);

      hasBeenScannedRecently.value = false;
    }, 3000);
  }
});

watchEffect(async () => {
  qrImageDataUrl.value = await QRCode.toDataURL(qrUrl.value, {
    color: { light: props.barcodeBackgroundColor, dark: props.barcodeForegroundColor },
  });
});
</script>

<template>
  <div
    class="barcode-qr Y"
    :class="{
      'spin-effect': hasBeenScannedRecently && props.qrSuccess === 'sp',
      'successful-scan': hasBeenScannedRecently && props.qrSuccess === 'em',
    }"
  >
    <img class="barcode-qr__code" :src="qrImageDataUrl" />
  </div>
</template>

<style scoped lang="scss">
@use './barcodeqr.scss';
</style>
